import type { Cart } from "./types";

const PREFIX = "emynor.storefront.unfinished-checkout.v1";
const VERSION = 1;

type CheckoutStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface UnfinishedCheckoutCart {
  attemptId: string;
  orderId: string;
  cart: Cart;
}

export function unfinishedCheckoutCartKey(
  tenantId: string,
  customerId: string,
): string {
  return [PREFIX, tenantId, customerId].map(encodeURIComponent).join(":");
}

export function readUnfinishedCheckoutCart(
  storage: CheckoutStorage,
  key: string,
): UnfinishedCheckoutCart | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as unknown;
    if (!isRecord(value) || value["version"] !== VERSION) {
      storage.removeItem(key);
      return null;
    }
    const snapshot = value["snapshot"];
    const cart = isRecord(snapshot) ? snapshot["cart"] : null;
    if (
      !isRecord(snapshot) ||
      typeof snapshot["attemptId"] !== "string" ||
      typeof snapshot["orderId"] !== "string" ||
      !isRecord(cart) ||
      !isRecord(cart["orderType"]) ||
      (cart["orderType"]["type"] !== "delivery" &&
        cart["orderType"]["type"] !== "pickup") ||
      typeof cart["orderType"]["branchId"] !== "string" ||
      !Array.isArray(cart["lines"]) ||
      cart["lines"].length === 0 ||
      !cart["lines"].every(
        (line) =>
          isRecord(line) &&
          typeof line["id"] === "string" &&
          typeof line["itemId"] === "string" &&
          typeof line["slug"] === "string" &&
          isRecord(line["name"]) &&
          typeof line["name"]["ar"] === "string" &&
          typeof line["name"]["en"] === "string" &&
          typeof line["image"] === "string" &&
          typeof line["unitPrice"] === "number" &&
          Array.isArray(line["additions"]) &&
          typeof line["quantity"] === "number" &&
          line["quantity"] > 0,
      )
    ) {
      storage.removeItem(key);
      return null;
    }
    return snapshot as unknown as UnfinishedCheckoutCart;
  } catch {
    tryRemove(storage, key);
    return null;
  }
}

export function writeUnfinishedCheckoutCart(
  storage: CheckoutStorage,
  key: string,
  snapshot: UnfinishedCheckoutCart,
): void {
  try {
    storage.setItem(key, JSON.stringify({ version: VERSION, snapshot }));
  } catch {
    // Browser storage can be unavailable; the payment flow must still continue.
  }
}

export function removeUnfinishedCheckoutCart(
  storage: CheckoutStorage,
  key: string,
  attemptId: string,
): void {
  if (readUnfinishedCheckoutCart(storage, key)?.attemptId === attemptId) {
    tryRemove(storage, key);
  }
}

export function updateUnfinishedCheckoutAttempt(
  storage: CheckoutStorage,
  key: string,
  previousAttemptId: string,
  nextAttemptId: string,
): void {
  const snapshot = readUnfinishedCheckoutCart(storage, key);
  if (snapshot?.attemptId === previousAttemptId) {
    writeUnfinishedCheckoutCart(storage, key, {
      ...snapshot,
      attemptId: nextAttemptId,
    });
  }
}

export function canMergeUnfinishedCart(current: Cart, saved: Cart): boolean {
  return (
    current.lines.length === 0 ||
    (current.orderType?.type === saved.orderType?.type &&
      current.orderType?.branchId === saved.orderType?.branchId)
  );
}

export function recoverUnfinishedCart(current: Cart, saved: Cart): Cart {
  if (current.lines.length === 0) return saved;
  if (!canMergeUnfinishedCart(current, saved)) return current;
  const currentIds = new Set(current.lines.map((line) => line.id));
  return {
    ...current,
    lines: [
      ...saved.lines.filter((line) => !currentIds.has(line.id)),
      ...current.lines,
    ],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function tryRemove(storage: CheckoutStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Storage can be unavailable in strict privacy modes.
  }
}
